import { Alert, AlertIcon, Box, CloseButton } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FoaPart, ProcedureType } from "../../../../../autogen/bff-api";
import { getProcedureLabel } from "../../../portal/utils/displayFormatters";

interface Props {
  procedureType: ProcedureType | null | undefined;
  foaPart: FoaPart | null | undefined;
  namesNoProcedure?: boolean;
}

interface NamedProcedure {
  procedureType: ProcedureType;
  namesNoProcedure: boolean;
}

interface ProcedureChange {
  from: NamedProcedure;
  to: NamedProcedure;
}

export const ProcedureChangedNotice = ({ procedureType, foaPart, namesNoProcedure = false }: Props) => {
  const { t } = useTranslation();
  const [change, dismiss] = useServerProcedureChange(procedureType ?? null, namesNoProcedure);

  if (!change) return null;
  const label = ({ procedureType, namesNoProcedure }: NamedProcedure) =>
    getProcedureLabel(procedureType, t, { namesNoProcedure });
  return (
    <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
      <AlertIcon />
      <Box flex="1">
        {foaPart
          ? t("The procedure changed from {{from}} to {{to}} because the procurement is now {{part}}", {
              from: label(change.from),
              to: label(change.to),
              part: t(foaPart),
            })
          : t("The procedure changed from {{from}} to {{to}}", { from: label(change.from), to: label(change.to) })}
      </Box>
      <CloseButton size="md" aria-label={t("Close") ?? undefined} onClick={dismiss} />
    </Alert>
  );
};

const useServerProcedureChange = (
  procedureType: ProcedureType | null,
  namesNoProcedure: boolean,
): [ProcedureChange | null, () => void] => {
  const [change, setChange] = useState<ProcedureChange | null>(null);
  const previous = useRef<NamedProcedure | null>(procedureType && { procedureType, namesNoProcedure });

  useEffect(() => {
    const before = previous.current;
    const current = procedureType && { procedureType, namesNoProcedure };
    previous.current = current;
    if (before == null || current == null || before.procedureType === current.procedureType) return;
    setChange((shown) => {
      const from = shown?.from ?? before;
      return from.procedureType === current.procedureType ? null : { from, to: current };
    });
  }, [procedureType, namesNoProcedure]);

  return [change, () => setChange(null)];
};
